package org.teddapp.project.ui.login

import androidx.compose.ui.graphics.SolidColor

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.teddapp.project.auth.BiometricAuthenticator
import org.teddapp.project.auth.BiometricResult
import org.teddapp.project.db.repo.AccountRepository
import org.teddapp.project.db.repo.LoginResult
import org.teddapp.project.db.repo.SignupResult
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.teddPasswordTransformation

/**
 * Briques de la connexion en mise en page compacte : cercle d'empreinte, bandeau d'erreur, titre dégradé.
 */

// ──────────────────────────────────────────────────────────────────────
// Sous-composants
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun PhoneBiometricCircle(
    state: LoginScanState,
    onTap: () -> Unit,
) {
    val isScanning = state == LoginScanState.Scanning
    val isSuccess = state == LoginScanState.Success
    val isFailed = state == LoginScanState.Failed

    Box(
        modifier = Modifier
            .size(140.dp)
            .clip(CircleShape)
            .then(
                when {
                    isSuccess -> Modifier.background(
                        SolidColor(TeddTokens.Success)
                    )
                    isFailed -> Modifier.background(
                        SolidColor(TeddTokens.Danger)
                    )
                    isScanning -> Modifier.background(
                        Brush.radialGradient(listOf(TeddTokens.LimeStrong, TeddTokens.LimeStrong))
                    )
                    else -> Modifier
                        .background(TeddTokens.LimeStrong.copy(alpha = 0.10f))
                        .border(2.dp, TeddTokens.LimeStrong.copy(alpha = 0.25f), CircleShape)
                }
            )
            .clickable(
                enabled = state == LoginScanState.Idle || isFailed,
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onTap,
            ),
        contentAlignment = Alignment.Center,
    ) {
        when {
            isSuccess -> PhoneCheckMark(size = 56.dp, color = TeddTokens.OnAction)
            else -> PhoneFingerprint(
                size = 64.dp,
                color = if (isScanning || isFailed) TeddTokens.OnAction else TeddTokens.LimeStrong,
            )
        }
    }
}

@Composable
internal fun PhoneFingerprint(size: androidx.compose.ui.unit.Dp, color: Color) {
    Canvas(modifier = Modifier.size(size)) {
        val s = this.size.minDimension
        val sx = s / 60f
        val stroke = 1.8f * sx
        val cx = 30f * sx
        val cy = 36f * sx
        listOf(22f to 200f, 18f to 180f, 14f to 160f, 10f to 140f, 6f to 120f).forEach { (r, sweep) ->
            val rPx = r * sx
            drawArc(
                color = color,
                startAngle = 180f - (sweep / 2f - 90f),
                sweepAngle = sweep,
                useCenter = false,
                topLeft = Offset(cx - rPx, cy - rPx),
                size = Size(rPx * 2, rPx * 2),
                style = Stroke(width = stroke, cap = StrokeCap.Round),
            )
        }
    }
}

@Composable
internal fun PhoneCheckMark(size: androidx.compose.ui.unit.Dp, color: Color) {
    Canvas(modifier = Modifier.size(size)) {
        val s = this.size.minDimension
        val sx = s / 24f
        drawLine(color, Offset(5f * sx, 12f * sx), Offset(9f * sx, 16f * sx), strokeWidth = 3.dp.toPx(), cap = StrokeCap.Round)
        drawLine(color, Offset(9f * sx, 16f * sx), Offset(19f * sx, 6f * sx), strokeWidth = 3.dp.toPx(), cap = StrokeCap.Round)
    }
}

@Composable
internal fun PhoneErrorBanner(message: String, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.DangerSoft)
            .border(1.dp, TeddTokens.Danger.copy(alpha = 0.3f), RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(TeddTokens.Danger))
        Text(
            text = message,
            color = TeddTokens.Danger,
            fontSize = 12.5.sp,
            fontWeight = FontWeight.Medium,
        )
    }
}

@Composable
internal fun PhoneGradientText(
    text: String,
    fontSize: androidx.compose.ui.unit.TextUnit,
    fontWeight: FontWeight = FontWeight.Normal,
    letterSpacing: androidx.compose.ui.unit.TextUnit = 0.sp,
) {
    Text(
        text = text,
        style = androidx.compose.ui.text.TextStyle(
            color = TeddTokens.Ink,
            fontSize = fontSize,
            fontWeight = fontWeight,
            letterSpacing = letterSpacing,
        ),
    )
}
